/**
 * Búsquedas recientes del asesor.
 *
 * Guarda la INTENCIÓN (los parámetros), nunca los resultados: los parámetros no
 * vencen, los precios sí. Una card que mostrara "desde EUR 90" sería la forma
 * más rápida de que alguien cotice media hora tarde un precio que ya no existe
 * — el provenance del hub dura 15 minutos justamente por eso.
 *
 * Se guardan todas juntas con su vertical y cada buscador muestra las suyas: en
 * `/trenes` ver búsquedas de autos es ruido. Que el almacén sea uno solo deja
 * la puerta abierta a un panel global sin migrar nada.
 */

/** Cuántas se conservan POR VERTICAL. Más que esto no se escanea de un vistazo. */
export const MAX_PER_VERTICAL = 5;

export interface RecentSearch {
  vertical: string;
  /** Los query params de la búsqueda, tal como viven en la URL. */
  params: Record<string, string>;
  /**
   * Fecha del viaje (`YYYY-MM-DD`), si el vertical la tiene. Sirve para que la
   * búsqueda se caiga sola cuando el viaje ya salió. eSIMs y pases no la
   * tienen: esas solo se van por el tope.
   */
  travelDate?: string;
  savedAt: number;
}

/** Dos búsquedas son la misma si piden lo mismo, sin importar el orden. */
export function sameSearch(a: RecentSearch, b: RecentSearch): boolean {
  if (a.vertical !== b.vertical) return false;
  const keys = new Set([...Object.keys(a.params), ...Object.keys(b.params)]);
  for (const key of keys) {
    if (a.params[key] !== b.params[key]) return false;
  }
  return true;
}

/**
 * Deja la lista lista para mostrar: saca las que ya viajaron y recorta al tope
 * POR VERTICAL, conservando las más nuevas.
 *
 * `today` entra por parámetro (no `new Date()` adentro) para que la caducidad
 * se pueda probar sin depender del reloj de quien corra los tests.
 */
export function pruneRecents(
  list: RecentSearch[],
  today: string,
): RecentSearch[] {
  const vigentes = list.filter(
    // Sin fecha no caduca: una eSIM para España sigue sirviendo.
    (item) => !item.travelDate || item.travelDate >= today,
  );

  const porVertical = new Map<string, number>();
  return vigentes
    .slice()
    .sort((a, b) => b.savedAt - a.savedAt)
    .filter((item) => {
      const usadas = porVertical.get(item.vertical) ?? 0;
      if (usadas >= MAX_PER_VERTICAL) return false;
      porVertical.set(item.vertical, usadas + 1);
      return true;
    });
}

/**
 * Suma una búsqueda. Repetir los mismos parámetros NO duplica: la sube al tope,
 * que es lo que el asesor espera de una lista de recientes.
 */
export function addRecent(
  list: RecentSearch[],
  entry: RecentSearch,
  today: string,
): RecentSearch[] {
  const sinRepetida = list.filter((item) => !sameSearch(item, entry));
  return pruneRecents([entry, ...sinRepetida], today);
}

export function removeRecent(
  list: RecentSearch[],
  entry: RecentSearch,
): RecentSearch[] {
  return list.filter((item) => !sameSearch(item, entry));
}

/** Las de un vertical, de la más nueva a la más vieja. */
export function recentsFor(
  list: RecentSearch[],
  vertical: string,
): RecentSearch[] {
  return list
    .filter((item) => item.vertical === vertical)
    .sort((a, b) => b.savedAt - a.savedAt);
}

/** "2026-09-20" de hoy, en hora local: la fecha de viaje también es local. */
export function todayISO(now = new Date()): string {
  const mes = String(now.getMonth() + 1).padStart(2, '0');
  const dia = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mes}-${dia}`;
}

/**
 * La agencia entra en la clave: cambiar de agencia no puede mostrar las
 * búsquedas de la otra (21.2). Son solo parámetros, no hay precios ni netos,
 * pero mezclarlas igual confunde.
 */
export function storageKey(agencyId: number | string): string {
  return `rutero:recentSearches:${agencyId}`;
}

export function loadRecents(agencyId: number | string): RecentSearch[] {
  try {
    const raw = localStorage.getItem(storageKey(agencyId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Contenido de storage: puede venir de una versión vieja o editado a mano.
    return parsed.filter(
      (item): item is RecentSearch =>
        Boolean(item) &&
        typeof item === 'object' &&
        typeof (item as RecentSearch).vertical === 'string' &&
        typeof (item as RecentSearch).savedAt === 'number' &&
        Boolean((item as RecentSearch).params) &&
        typeof (item as RecentSearch).params === 'object',
    );
  } catch {
    // Storage bloqueado o JSON roto: sin recientes se sigue trabajando igual.
    return [];
  }
}

export function persistRecents(
  agencyId: number | string,
  list: RecentSearch[],
): void {
  try {
    localStorage.setItem(storageKey(agencyId), JSON.stringify(list));
  } catch {
    /* sin memoria: ver arriba */
  }
}
